// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { formValueSelector, change as reduxFormChange } from 'redux-form';
import { FormattedMessage } from 'translations';
import RoundedHeader from 'app/components/base/RoundedHeader';
import PaymentButton from 'mz-ui-kit/form/PaymentButton';
import CouponCodeField from 'mz-ui-kit/form/CouponCodeField/field';
import { useStripeConfig } from 'app/utils/stripe';
import { Container, ButtonWrapper, CouponCodeWrapper } from './elements';
import config from 'config';
import {
  getLoggedIn,
  getStripePricing,
  getDisplayPrice,
  getCustomersPaymentOptionId,
  behavesAsTravelAgent,
  isTC,
  isPriceUpdating,
  getPriceComponents,
  getBookingAttempts,
} from 'app/sagas/selectors';
import {
  BOOK_TRIP_FORM,
  COUPON_CODE_FIELD_NAME,
  PRICE_FIELD_NAME,
} from 'app/constants';
import { addCoupon, changeCardBrand } from 'app/actions/book-trip';
import { useEventOnce } from 'app/hooks';
import { pushData } from 'app/utils/analytics';

const messages = {
  title: { id: 'BOOK_TRIP.SECURE_PAYMENT.TITLE' },
};

export const SecurePayment = ({
  onClick,
  loading,
  disabled,
  priceUpdating,
  isLoggedIn,
  isAgentUser,
  isTravelCounselor,
  shouldSecurePayment,
  currency,
  postPayment,
  displayCurrency,
  displayAmount,
  stripeChargeAmount,
  selectedPaymentId,
  renderBeforeButton,
  supportsCreditCard,
  supportsDirectBill,
  priceComponents,
  discountAmount,
  changeFieldValue,
  onChangeCardBrand = () => {},
  onAddCoupon,
  couponCode,
  bookingAttempts,
}) => {
  const agentOptions = selectedPaymentId ? [selectedPaymentId] : [];
  const allowedPaymentOptionIds = isAgentUser ? agentOptions : null;
  const stripeConfig = useStripeConfig();
  const { handleEventOnce } = useEventOnce((data) =>
    pushData('begin-payment-started-filling-payment-info', data)
  );

  const handleChangeCardBrand = (...args) => {
    onChangeCardBrand(...args);
    handleEventOnce({
      currency,
      totalPrice: displayAmount,
    });
  };

  return (
    <Container>
      <RoundedHeader icon={{ family: 'mozio', name: 'payment' }}>
        <FormattedMessage {...messages.title} />
      </RoundedHeader>
      {currency && (
        <ButtonWrapper>
          <CouponCodeWrapper>
            <CouponCodeField
              ondemand={priceComponents.ondemand}
              searchId={priceComponents.session_id}
              tripId={priceComponents.trip_id}
              discountAmount={discountAmount}
              input={{
                value: couponCode,
                onChange: (coupon) => {
                  changeFieldValue(
                    BOOK_TRIP_FORM,
                    COUPON_CODE_FIELD_NAME,
                    coupon
                  );
                  onAddCoupon();
                },
              }}
            />
          </CouponCodeWrapper>
          <PaymentButton
            postPayment={postPayment}
            searchId={priceComponents.session_id}
            tripId={priceComponents.trip_id}
            devMode={config.STRIPE_DEVELOPMENT_MODE}
            stripeConfigs={stripeConfig}
            onClick={onClick}
            loading={loading || priceUpdating}
            disabled={disabled || priceUpdating}
            currency={currency}
            onChangeCardBrand={handleChangeCardBrand}
            originalCurrency={displayCurrency}
            displayAmount={displayAmount}
            canSaveCreditCard={isLoggedIn && !isAgentUser}
            stripeChargeAmount={stripeChargeAmount}
            renderBeforeButton={renderBeforeButton}
            hideCvcField={isTravelCounselor}
            allowedPaymentOptionIds={allowedPaymentOptionIds}
            shouldSecurePayment={shouldSecurePayment}
            paymentIntentInput={[bookingAttempts]}
            allowedPaymentMethods={[
              supportsCreditCard && isLoggedIn ? 'savedCard' : null,
              supportsCreditCard ? 'newCard' : null,
              supportsDirectBill ? 'directBill' : null,
            ]}
          />
        </ButtonWrapper>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => {
  const pricing = getStripePricing(state);
  const selector = formValueSelector(BOOK_TRIP_FORM);
  const price = selector(state, PRICE_FIELD_NAME);

  return {
    isPriceUpdating: isPriceUpdating(state),
    isLoggedIn: getLoggedIn(state),
    isAgentUser: behavesAsTravelAgent(state),
    isTravelCounselor: isTC(state),
    shouldSecurePayment: pricing.forceThreeDSecure,
    currency: pricing.currency,
    displayCurrency: pricing.displayCurrency,
    displayAmount: getDisplayPrice(state),
    stripeChargeAmount: pricing.amount,
    selectedPaymentId: getCustomersPaymentOptionId(state),
    supportsCreditCard: pricing.supports.creditCard,
    supportsDirectBill: pricing.supports.directBill,
    priceComponents: getPriceComponents(state),
    postPayment: price.postPayment,
    couponCode: price.couponCode,
    discountAmount: price.couponCode ? `- ${price.couponDiscount}` : '',
    bookingAttempts: getBookingAttempts(state),
  };
};

const mapDispatchToProps = {
  changeFieldValue: reduxFormChange,
  onAddCoupon: addCoupon,
  onChangeCardBrand: changeCardBrand,
};

export default connect(mapStateToProps, mapDispatchToProps)(SecurePayment);
